import type React from 'react';
import {useState} from 'react';
import {Box, Button, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {useCategorySettingsActions} from '../hooks/useCategorySettings';
import type {DatabagBuild} from '../types/settingsTypes';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'});
const when = (v: string | null) => v ? WHEN.format(new Date(v)) : 'Never';

/** Facts and benchmark builds per dataset: settings marked "After rebuild" apply once a dataset is rebuilt. */
export const BuildsSettings: React.FC<{builds: DatabagBuild[]}> = ({builds}) => {
    const actions = useCategorySettingsActions();
    const [busy, setBusy] = useState<number | null>(null);
    return (
        <Paper variant="outlined" id="settings-builds" sx={{p: 2, scrollMarginTop: 16}}>
            <Typography variant="h6" component="h2">Builds</Typography>
            <Typography variant="body2" color="text.secondary">
                Category facts and internal benchmarks are rebuilt nightly. Rebuild a dataset here to apply document-type
                and benchmark settings now.
            </Typography>
            <Box sx={{overflowX: 'auto', mt: 1}}>
                <Table size="small" aria-label="Builds per dataset">
                    <TableHead>
                        <TableRow>
                            <TableCell>Dataset</TableCell>
                            <TableCell>Facts built</TableCell>
                            <TableCell>Latest month</TableCell>
                            <TableCell>Benchmarks built</TableCell>
                            <TableCell align="right">Item groups</TableCell>
                            <TableCell/>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {builds.map(b => (
                            <TableRow key={b.databagId}>
                                <TableCell>{`[${b.databagId}] ${b.name}`}</TableCell>
                                <TableCell>{when(b.factsBuiltAt)}</TableCell>
                                <TableCell>{b.latestMonth?.slice(0, 7) ?? '—'}</TableCell>
                                <TableCell>{when(b.benchmarksBuiltAt)}</TableCell>
                                <TableCell align="right">{b.benchmarkGroups.toLocaleString(LOCALE)}</TableCell>
                                <TableCell align="right">
                                    <Button size="small" disabled={busy !== null} onClick={() => {
                                        setBusy(b.databagId);
                                        void actions.rebuild(b.databagId, b.name).finally(() => setBusy(null));
                                    }}>
                                        {busy === b.databagId ? 'Rebuilding…' : 'Rebuild'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
        </Paper>
    );
};
